import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Avatar,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { gamoraApi, ChatTokenUsage, getUniqueRegulatoryBodies, regulationsApi, DetectedForm, MDRCitation, EudamedDevicesRequest } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { 
  AccountCircle, 
  Send,
  Download,
  Launch,
  Visibility,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FeedbackButtons from './FeedbackButtons';
import TokenRechargeModal from './TokenRechargeModal';
import WorkflowCreator from './WorkflowCreator';
import ConvertToWorkflowDialog from './ConvertToWorkflowDialog';
import FormSuggestionComponent from './FormSuggestion';
import AILiteracyDisclaimer from './AILiteracyDisclaimer';
import { MDRCitationList } from './MDRCitation';
import { ComplianceWorkflow, workflowService, ChatMessage as WorkflowChatMessage, ChatWorkflowCreateRequest } from '../services/workflowService';
import { WorkflowStepsPanel } from './WorkflowStepsPanel';
import ShareDialog from './ShareDialog';
import { formSuggestionService, FormSuggestion } from '../services/formSuggestionService';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  regulatoryBodies?: string[];
  generationId?: string; // Add this field for feedback
  questionComplexity?: 'simple' | 'complex' | 'process'; // Add complexity classification
  forms?: DetectedForm[]; // Forms detected from the query
  references?: Array<{ url: string; country: string }> | string[]; // References from the response
  citations?: MDRCitation[]; // MDR section citations
  suggestedForms?: FormSuggestion[]; // Suggested forms for this specific question
  suggestedFormsJurisdiction?: string; // Jurisdiction used when generating suggestions
  workflowId?: string; // Auto-created workflow for this answer
  workflowName?: string; // Auto-created workflow name for this answer
  generateAnswer?: boolean; // Whether answer generation was enabled
  workflowSuggestion?: WorkflowSuggestion;
}

interface WorkflowSuggestion {
  score: number;
  reasons: string[];
  prompt: string; // user message that triggered this suggestion
  inferredJurisdiction?: string;
  deviceClass?: string;
}

const getSessionId = () => {
  let sessionId = localStorage.getItem('gamora_session_id');
  return sessionId || '';
};

interface SearchInterfaceProps {
  sessionId?: string;
  onSessionCreated?: (sessionId: string) => void;
  country?: string;
  selectedWorkflowId?: string;
  onWorkflowSelect?: (workflowId: string) => void;
}

const _inferJurisdictionFromText = (text: string): string | undefined => {
  const lower = text.toLowerCase();
  if (
    lower.includes('eu mdr') ||
    (lower.includes('eu') && lower.includes('mdr')) ||
    lower.includes('european union') ||
    /\bmdr\b/.test(lower) ||
    /\bce mark/.test(lower) ||
    /\bnotified body\b/.test(lower) ||
    /\beudamed\b/.test(lower) ||
    /\bannex\s+(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii|xiii|xiv|xv|xvi)\b/.test(lower)
  ) {
    return 'eu_mdr';
  }
  if (lower.includes('fda') || lower.includes('510(k)') || lower.includes('pma') || lower.includes('de novo')) {
    return 'us_fda';
  }
  return undefined;
};

const _inferDeviceClass = (text: string, jurisdiction?: string): string | undefined => {
  const lower = text.toLowerCase();
  const match = lower.match(/class\s*(iii|iiia|iib|iia|ii|i|a|b|c|d)\b/);
  if (!match) return undefined;
  const cls = match[1];
  if (jurisdiction === 'eu_mdr') {
    if (cls === 'iia') return 'class_iia';
    if (cls === 'iib') return 'class_iib';
    if (cls === 'i') return 'class_i';
    if (cls === 'iii') return 'class_iii';
  }
  if (jurisdiction === 'us_fda') {
    if (cls === 'i') return 'class_i';
    if (cls === 'ii') return 'class_ii';
    if (cls === 'iii') return 'class_iii';
  }
  return undefined;
};

const _analyzeWorkflowNeed = (prompt: string, jurisdictionHint?: string) => {
  const text = (prompt || '').toLowerCase();
  const reasons: string[] = [];
  let score = 0;

  // ── 1. Build / project intent ──
  // "I'm building / developing / designing / manufacturing / launching ..."
  // This is the strongest general signal — the user has a PRODUCT, not just a question.
  const hasBuildIntent =
    /\b(build|building|develop|developing|design|designing|create|creating|prototype|prototyping|manufactur|manufacturing|launch|launching|bring to market|commercializ|making|made|produce|producing|working on)\b/.test(text);
  if (hasBuildIntent) {
    score += 2;
    reasons.push('Project/build intent detected');
  }

  // ── 2. Regulatory context ──
  // Mentions any regulation, standard, or regulatory outcome
  const hasRegContext = /\bce\b|\bce mark|\beu mdr\b|\bmdr\b|\b510\(k\)|\bnotified body\b|\bapproval\b|\bconformity\b|\bclassif|\bregulat|\bcomplian|\bsubmission\b|\btechnical file\b|\bfda\b|\btga\b|\bhealth canada\b|\bcdsco\b|\bannex\b|\bclass\s+(i|ii|iii)\b/.test(text);
  if (hasRegContext) {
    score += 2;
    reasons.push('Regulatory context detected');
  }

  // ── 3. "medical device" or "device" — explicit product category ──
  const hasMedicalDevice = /\bmedical device\b|\bmed\s*device\b/.test(text);
  const hasDevice = /\bdevice\b/.test(text);
  if (hasMedicalDevice) {
    score += 2;
    reasons.push('Medical device explicitly mentioned');
  } else if (hasDevice) {
    score += 1;
    reasons.push('Device mentioned');
  }

  // ── 4. "I am building a <something>" pattern ──
  // Catches ANY product name — "pulse oxymeter", "smart watch", "blood analyzer", etc.
  // The pattern is: build-intent verb + article/determiner + noun phrase.
  // If the user says "I'm building a [whatever]" AND there's regulatory context,
  // that's almost certainly a workflow need, regardless of what [whatever] is.
  if (hasBuildIntent && hasRegContext) {
    score += 1;
    reasons.push('Build intent + regulatory context combined');
  }

  // ── 5. Specific compliance deliverables ──
  const hasDeliverables =
    /\bevidence mapping\b|\bstandards mapping\b|\btest vs justification\b|\btechnical documentation\b|\bannex ii\b|\brisk file\b|\bclinical evaluation\b|\bpms\b|\bpmcf\b|\bqms\b|\biso 13485\b|\biso 14971\b|\biec 60601\b|\biec 62304\b|\biec 62366\b|\bgspr\b|\brisk management\b|\bsafety and performance\b|\bcer\b/.test(text);
  if (hasDeliverables) {
    score += 2;
    reasons.push('Compliance deliverables requested');
  }

  // ── 6. Planning / multi-step intent ──
  const isStepByStep = /\bstep\b|\bsteps\b|\bplan\b|\btimeline\b|\bchecklist\b|\btrack\b|\bworkflow\b|\broadmap\b|\bprocess\b|\bhow to\b|\bprocedure\b|\bguide\b/.test(text);
  if (isStepByStep) {
    score += 1;
    reasons.push('Multi-step / planning intent detected');
  }

  // ── 7. "I am building a <something>" without explicit regulation ──
  // If the user says "I am building a pulse oxymeter" (no MDR/FDA mentioned),
  // the build intent (+2) alone might not hit threshold.
  // Boost if there's a build verb followed by an article + noun (product pattern).
  const hasBuildProduct = /\b(build|building|develop|developing|design|designing|create|creating|manufactur|manufacturing|making|launch|launching|working on)\s+(a|an|the|my|our)\s+\w+/i.test(text);
  if (hasBuildProduct && !hasMedicalDevice && !hasDevice) {
    score += 1;
    reasons.push('Building a specific product');
  }

  // ── 8. Negative signal: pure definitional / comparison question ──
  const looksLikeSimpleQA = /^\s*(what is|what are|define|explain|difference between|compare|tell me about)\b/.test(text);
  if (looksLikeSimpleQA && !hasDeliverables && !hasRegContext && !hasBuildIntent) {
    score -= 2;
    reasons.push('Looks like a simple Q&A question');
  }

  const inferredJurisdiction = jurisdictionHint || _inferJurisdictionFromText(prompt);
  const deviceClass = inferredJurisdiction ? (_inferDeviceClass(prompt, inferredJurisdiction) || undefined) : undefined;

  return {
    score,
    reasons,
    inferredJurisdiction,
    deviceClass,
  };
};

const SearchInterface: React.FC<SearchInterfaceProps> = ({ sessionId: propSessionId, onSessionCreated, country: initialCountry = 'All', selectedWorkflowId, onWorkflowSelect }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const sendInProgressRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenHistory, setTokenHistory] = useState<ChatTokenUsage[]>([]);
  // Remove activeTab state since we're using sidebar selection instead
  const [showWorkflowCreator, setShowWorkflowCreator] = useState(false);
  const [showConvertToWorkflow, setShowConvertToWorkflow] = useState(false);
  const [showAIDisclaimer, setShowAIDisclaimer] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [activeTabs, setActiveTabs] = useState<{ [messageId: string]: number }>({});
  const [workflowCreateLoadingMessageId, setWorkflowCreateLoadingMessageId] = useState<string | null>(null);
  const [dismissedWorkflowSuggestionByMessageId, setDismissedWorkflowSuggestionByMessageId] = useState<Record<string, boolean>>({});
  const [aiNoticeDismissed, setAiNoticeDismissed] = useState(() => {
    return localStorage.getItem('gamora_ai_notice_dismissed') === 'true';
  });
  // Try to load selected regulation from localStorage, fallback to 'all'
  const [selectedRegulation, setSelectedRegulation] = useState<string>(() => {
    const saved = localStorage.getItem('gamora_selected_regulation');
    return saved || 'all';
  });
  const [regulations, setRegulations] = useState<any[]>([]);
  const [selectedWorkflowName, setSelectedWorkflowName] = useState<string>('');
  const [generateAnswer] = useState(true);
  const isSuperAdminUser = (() => {
    try {
      // Role 0 = SUPER_ADMIN (see `App.tsx` role system)
      const storedRole = localStorage.getItem('gamora_role');
      if (storedRole) {
        const roleNum = parseInt(storedRole, 10);
        if (!isNaN(roleNum)) return roleNum === 0;
      }

      const token = localStorage.getItem('gamora_token');
      if (!token) return false;
      const decoded = jwtDecode(token) as any;

      const roleCandidate =
        decoded?.role ??
        decoded?.user?.role ??
        decoded?.claims?.role ??
        decoded?.data?.role;
      const roleNum = parseInt(roleCandidate, 10);
      if (!isNaN(roleNum)) return roleNum === 0;

      // Backward-compatible fallback: a couple of hardcoded emails were used previously.
      const emailCandidate =
        decoded?.email ??
        decoded?.user?.email ??
        (typeof decoded?.sub === 'string' && decoded.sub.includes('@') ? decoded.sub : '') ??
        '';
      const email = (emailCandidate || '').toString().toLowerCase();
      return email === 'gandlurm@gmail.com' || email === 'gauriskundliya@gmail.com';
    } catch {
      return false;
    }
  })();
  
  // Save selectedRegulation to localStorage whenever it changes
  useEffect(() => {
    if (selectedRegulation && selectedRegulation !== 'all') {
      localStorage.setItem('gamora_selected_regulation', selectedRegulation);
    } else {
      localStorage.removeItem('gamora_selected_regulation');
    }
  }, [selectedRegulation]);

  // Fetch workflow name when selectedWorkflowId changes
  useEffect(() => {
    const fetchWorkflowName = async () => {
      if (selectedWorkflowId) {
        try {
          const workflowStatus = await workflowService.getWorkflowStatus(selectedWorkflowId);
          if (workflowStatus?.workflow) {
            setSelectedWorkflowName(workflowStatus.workflow.name || '');
          }
        } catch (err) {
          console.error('Error fetching workflow:', err);
          setSelectedWorkflowName('');
        }
      } else {
        setSelectedWorkflowName('');
      }
    };
    fetchWorkflowName();
  }, [selectedWorkflowId]);

  // Workflow handlers
  const handleWorkflowCreated = (workflow: ComplianceWorkflow) => {
    // Automatically select and open the newly created workflow
    if (workflow?.id && onWorkflowSelect) {
      onWorkflowSelect(workflow.id);
    }
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [sessionId, setSessionId] = useState<string>(propSessionId || getSessionId());
  const hasCreatedSession = useRef(false);
  const [showTokenRecharge, setShowTokenRecharge] = useState(false);
  const [currentTokens, setCurrentTokens] = useState(0);
  const [requiredTokens, setRequiredTokens] = useState(0);
  const isCreatingSession = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // If propSessionId changes, update sessionId state
  useEffect(() => {
    if (propSessionId !== sessionId) {
      setSessionId(propSessionId || '');
      // Don't clear messages if a send is in progress (we just created this session)
      if (!sendInProgressRef.current) {
        setMessages([]);
        setTokenHistory([]);
        setInputValue('');
        setError(null);
      }
      if (!propSessionId) {
        hasCreatedSession.current = false;
        isCreatingSession.current = false;
      }
      if (!sendInProgressRef.current) {
        setTimeout(() => scrollToTop(), 100);
      }
    }
  }, [propSessionId, sessionId]);

  // Load regulations from regulations table
  useEffect(() => {
    const loadRegulations = async () => {
      try {
        // Use public endpoint that's accessible to all authenticated users
        const data = await regulationsApi.getPublicRegulatoryAuthorities();
        const filteredRegs = (data.regulations || []).filter((reg: any) =>
          ['us_fda', 'eu_mdr'].includes(reg.jurisdiction)
        );
        setRegulations(filteredRegs);
        
        // Auto-detect country from IP and set default regulatory authority
        try {
          const detected = await regulationsApi.detectCountryFromIP();
          if (detected.regulation_id && filteredRegs.find((r: any) => r.id === detected.regulation_id)) {
            setSelectedRegulation(detected.regulation_id);
          }
        } catch (ipErr) {
          // Could not detect country from IP, using default
        }
      } catch (err) {
        console.error('Error loading regulatory authorities:', err);
        // Fallback: try the regular endpoint if public endpoint fails
        try {
          const data = await regulationsApi.getAllRegulations({
            status: 'active',
            limit: 100,
          });
          const filteredRegs = data.regulations.filter((reg: any) => 
            ['us_fda', 'eu_mdr'].includes(reg.jurisdiction)
          );
          setRegulations(filteredRegs);
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
        }
      }
    };
    loadRegulations();
  }, []);

  // Get regulatory authorities from regulations for dropdown
  const getRegulationOptions = () => {
    // Map country codes to display labels
    const countryMap: { [key: string]: string } = {
      'US': 'United States (FDA)',
      'EU': 'European Union (European Commission)',
    };

    // Return unique regulatory authorities from regulations
    if (!regulations || regulations.length === 0) {
      return [];
    }

    const options = regulations
      .map((reg: any) => ({
        code: reg.id,
        label: countryMap[reg.country] || `${reg.regulatory_authority} (${reg.country})`,
        country: reg.country,
        authority: reg.regulatory_authority
      }))
      .filter((opt, index, self) => 
        index === self.findIndex((o) => o.country === opt.country)
      ); // Get unique by country
    
    return options;
  };

  // Selected regulation is used directly in the composer; no separate derived value needed.


  useEffect(() => {
    if (messages.length === 0 && !loading) {
      setTimeout(() => scrollToTop(), 50);
    } else {
      scrollToBottom();
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem(`gamora_active_tabs_${sessionId}`, JSON.stringify(activeTabs));
  }, [activeTabs, sessionId]);

  // On mount, if sessionId is empty, don't create a session automatically
  // Sessions will be created when the first message is sent
  useEffect(() => {
    if (!sessionId) {
      // Clear any existing session data
      setMessages([]);
      setTokenHistory([]);
      setInputValue('');
      setError(null);
      // Scroll to top on initial mount with no session
      setTimeout(() => scrollToTop(), 100);
    }
  }, [sessionId]);

  // Track if we've already loaded history for this session to prevent reloading on every render
  const historyLoadedRef = useRef<string | null>(null);
  
  useEffect(() => {
    // If propSessionId is set but different from sessionId, we're switching sessions
    // But skip if a send is in progress (we just created this session ourselves)
    if (propSessionId && propSessionId !== sessionId) {
      if (!sendInProgressRef.current) {
        setLoadingHistory(true);
        setMessages([]);
        historyLoadedRef.current = null;
      }
      return;
    }
    
    // Load chat history and token usage when sessionId changes
    if (!sessionId) {
      historyLoadedRef.current = null;
      setLoadingHistory(false);
      return;
    }
    
    // Only load history if this is a session that was explicitly selected
    // AND we haven't already loaded it AND no send is in progress
    const shouldLoadHistory = propSessionId === sessionId && historyLoadedRef.current !== sessionId && !sendInProgressRef.current;
    
    if (shouldLoadHistory) {
      historyLoadedRef.current = sessionId; // Mark as loaded before async call
      setLoadingHistory(true); // Start loading history
      (async () => {
        try {
          const history = await gamoraApi.getChatHistory(sessionId);
          const mapped = history.map((m, idx) => {
            const base: Message = {
              id: m.id.toString(),
              type: m.sender === 'user' ? 'user' : 'assistant',
              content: m.message,
              timestamp: new Date(m.timestamp),
              generationId: m.generation_id,
              forms: m.forms,
              references: m.references,
              workflowId: m.workflow_id,
              workflowName: m.workflow_name,
            };
            if (base.type === 'assistant' && !base.workflowId) {
              const prevUser = history.slice(0, idx).reverse().find(p => p.sender === 'user');
              if (prevUser) {
                const need = _analyzeWorkflowNeed(prevUser.message);
                if (need.score >= 3) {
                  (base as any).workflowSuggestion = {
                    score: need.score,
                    reasons: need.reasons,
                    prompt: prevUser.message,
                    inferredJurisdiction: need.inferredJurisdiction,
                    deviceClass: need.deviceClass,
                  } as WorkflowSuggestion;
                }
              }
            }
            return base;
          });
          setMessages(mapped);
          const storedTabs = localStorage.getItem(`gamora_active_tabs_${sessionId}`);
          if (storedTabs) {
            try {
              const parsed = JSON.parse(storedTabs);
              // For super-admin users, default assistant messages with workflows to the Workflow tab
              // (and avoid stale per-session tab state forcing "Answer" to appear first/selected).
              if (isSuperAdminUser && parsed && typeof parsed === 'object') {
                const next = { ...(parsed as any) } as Record<string, number>;
                for (const m of history) {
                  if (!m) continue;
                  const msgId = (m.id ?? '').toString();
                  if (!msgId) continue;
                  if (m.workflow_id) {
                    next[msgId] = 0; // Workflow tab
                  }
                }
                setActiveTabs(next);
              } else {
                setActiveTabs(parsed);
              }
            } catch {
              setActiveTabs({});
            }
          } else {
            setActiveTabs({});
          }
          const tokens = await gamoraApi.getChatTokens(sessionId);
          setTokenHistory(tokens);
        } catch (e: any) {
          // If session not found (404), create a new session
          if (e?.response?.status === 404 && !isCreatingSession.current) {
            isCreatingSession.current = true;
            historyLoadedRef.current = null; // Reset so we can load the new session
            try {
              const response = await gamoraApi.createNewSession();
              const newSessionId = response.session_id;
              localStorage.setItem('gamora_session_id', newSessionId);
              // Use callback to avoid dependency issues
              setSessionId(prevId => {
                if (prevId !== newSessionId) {
                  // Clear messages and tokens for new session
                  setMessages([]);
                  setTokenHistory([]);
                  onSessionCreated?.(newSessionId); // Call the prop
                  return newSessionId;
                }
                return prevId;
              });
            } catch (createError) {
              console.error('Failed to create new session:', createError);
            } finally {
              isCreatingSession.current = false;
            }
          } else {
            console.error('Error loading chat history:', e);
          }
        } finally {
          setLoadingHistory(false); // Stop loading history
        }
      })();
    } else if (!shouldLoadHistory && propSessionId && propSessionId !== sessionId) {
      if (!sendInProgressRef.current) {
        historyLoadedRef.current = null;
        setLoadingHistory(false);
        setMessages([]);
      }
    } else if (!propSessionId) {
      if (!sendInProgressRef.current) {
        setMessages([]);
        setTokenHistory([]);
        setLoadingHistory(false);
      }
    }
  }, [sessionId, propSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add question complexity analysis function
  const analyzeQuestionComplexity = (question: string): 'simple' | 'complex' | 'process' => {
    const questionLower = question.toLowerCase();
    
    // Process/plan related questions - need full structured formatting
    const processKeywords = [
      'how to', 'process', 'steps', 'procedure', 'workflow', 'timeline', 'checklist',
      'implementation', 'plan', 'strategy', 'approach', 'method', 'guide', 'tutorial',
      'requirements', 'compliance', 'approval', 'registration', 'certification'
    ];
    
    // Simple questions - basic formatting is sufficient
    const simpleKeywords = [
      'what is', 'define', 'explain', 'describe', 'tell me about', 'meaning of',
      'difference between', 'compare', 'list', 'examples of'
    ];
    
    // Check for process/plan questions first (highest priority)
    if (processKeywords.some(keyword => questionLower.includes(keyword))) {
      return 'process';
    }
    
    // Check for simple questions
    if (simpleKeywords.some(keyword => questionLower.includes(keyword))) {
      return 'simple';
    }
    
    // Default to complex for regulatory questions that need structured answers
    return 'complex';
  };

  // Enhanced content formatting based on question complexity
  const formatContentBasedOnComplexity = (
    content: string, 
    complexity: 'simple' | 'complex' | 'process'
  ): string => {
    let formattedContent = content;
    const isProcess = complexity === 'process';
    
    // Common formatting for all complexity levels
    // 1. Clean up excessive newlines
    formattedContent = formattedContent.replace(/\n{3,}/g, '\n\n');
    
    // 2. Convert lines ending with ':' to proper markdown headings (skip for process output)
    if (!isProcess) {
      formattedContent = formattedContent.replace(/^(.+):$/gm, '### $1');
    } else {
      // Remove markdown headings/quotes in process outputs to avoid bold/styled blocks
      formattedContent = formattedContent.replace(/^#{1,6}\s+/gm, '');
      formattedContent = formattedContent.replace(/^>\s?/gm, '');
    }
    
    // 3. Format bullet points and lists
    formattedContent = formattedContent.replace(/^[-*•]\s+/gm, '- ');
    formattedContent = formattedContent.replace(/^\d+\.\s+/gm, (match) => match);
    
    // 4. Format or strip bold/italic patterns
    if (!isProcess) {
      formattedContent = formattedContent.replace(/\*\*(.+?)\*\*/g, '**$1**');
    } else {
      formattedContent = formattedContent.replace(/\*\*(.+?)\*\*/g, '$1');
      formattedContent = formattedContent.replace(/\*(.+?)\*/g, '$1');
    }
    
    // 5. Format specific patterns for regulatory content (skip for process output)
    if (!isProcess) {
      formattedContent = formattedContent.replace(/^(\d+\.\s*[A-Z][^:]+):/gm, '### $1');
      formattedContent = formattedContent.replace(/^([A-Z][^:]+):/gm, '### $1');
    }
    
    // 6. Ensure proper spacing around headings
    if (!isProcess) {
      formattedContent = formattedContent.replace(/(\n)(### .+)/g, '\n\n$2');
      formattedContent = formattedContent.replace(/(### .+)(\n)([^#\n])/g, '$1\n\n$3');
    }
    
    // 7. Clean up any remaining excessive spacing
    formattedContent = formattedContent.replace(/\n{2,}/g, '\n\n');
    
    // 8. Ensure proper line breaks for lists
    formattedContent = formattedContent.replace(/(\n- .+)(\n)(- )/g, '$1\n$3');
    
    return formattedContent.trim();
  };

  const inferJurisdictionFromText = _inferJurisdictionFromText;
  const inferDeviceClass = _inferDeviceClass;

  const buildWorkflowNameFromPrompt = (prompt: string, jurisdiction?: string) => {
    const labelMap: Record<string, string> = {
      eu_mdr: 'EU MDR',
      us_fda: 'US FDA',
      india_imdr: 'India IMDR',
      australia_tga: 'Australia TGA',
      canada_hc: 'Canada HC',
      iso: 'ISO',
    };
    const jurisdictionLabel = jurisdiction
      ? (labelMap[jurisdiction] || jurisdiction.replace('_', ' ').toUpperCase())
      : 'Workflow';
    const firstLine = prompt.split(/\n|\r|\./)[0].trim();
    const sanitized = firstLine.replace(/\s+/g, ' ').trim();
    // Avoid hardcoded "..." truncation; let UI handle truncation/wrapping based on available width.
    // Keep a generous cap to avoid pathological titles.
    const capped = sanitized.length > 160 ? sanitized.slice(0, 160).trim() : sanitized;
    const base = capped || 'New Workflow';
    return `${jurisdictionLabel} - ${base}`;
  };

  const analyzeWorkflowNeed = _analyzeWorkflowNeed;

  const EudamedTab: React.FC<{ workflowId?: string }> = ({ workflowId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);
    const [recordCount, setRecordCount] = useState<number>(0);
    const [queryUsed, setQueryUsed] = useState<string>('');

    useEffect(() => {
      let isActive = true;
      const load = async () => {
        if (!workflowId) {
          setError('No workflow selected.');
          return;
        }

        setLoading(true);
        setError(null);
        setRows([]);
        setRecordCount(0);
        setQueryUsed('');

        try {
          const status = await workflowService.getWorkflowStatus(workflowId);
          const steps = Array.isArray(status?.steps) ? status.steps : [];
          const classification =
            steps.find(step => (step.title || '').toString().toLowerCase().includes('classif')) ||
            steps.find(step => {
              const fields = (step?.configuration as any)?.fields;
              return Array.isArray(fields) && fields.some((f: any) => f?.key === 'intended_purpose');
            });

          const classificationInputs = (classification?.configuration as any)?.data_values || {};
          const workflowInputs = (status?.workflow as any)?.configuration?.workflow_inputs || {};

          const findFirstSavedValue = (key: string) => {
            const candidates: any[] = [];
            candidates.push(classificationInputs?.[key]);
            candidates.push(workflowInputs?.[key]);
            for (const s of steps) {
              const v = (s?.configuration as any)?.data_values?.[key];
              candidates.push(v);
            }
            for (const v of candidates) {
              const text = (v ?? '').toString().trim();
              if (text) return text;
            }
            return '';
          };

          const rawPurpose =
            findFirstSavedValue('intended_use') ||
            findFirstSavedValue('condition_addressed') ||
            findFirstSavedValue('intended_purpose');
          if (!rawPurpose) {
            setError('Add an intended use / condition addressed to load EUDAMED results.');
            setLoading(false);
            return;
          }

          const normalizedPurpose = rawPurpose.toLowerCase();

          const stopWords = new Set([
            'i', 'am', 'building', 'build', 'make', 'creating', 'create', 'a', 'an', 'the', 'of', 'for', 'to',
            'and', 'or', 'with', 'without', 'device', 'devices', 'monitor', 'monitoring', 'system', 'tool', 'app',
            'medical', 'patient', 'patients', 'use', 'used', 'intended', 'purpose', 'condition', 'addressed',
          ]);
          const tokens = rawPurpose
            .replace(/[^a-zA-Z0-9\s-]/g, ' ')
            .split(/\s+/)
            .map((token: string) => token.trim())
            .filter((token: string) => token.length > 2 && !stopWords.has(token.toLowerCase()));

          // EUDAMED's search is picky; use domain-aware fallbacks (try more specific clinical terms first).
          const terms: string[] = [];
          if (normalizedPurpose.includes('blood pressure') || /\bbp\b/.test(normalizedPurpose)) {
            terms.push('Blood pressure');
          }
          // Cardiac rhythm monitoring often appears in EUDAMED as ECG/electrocardiogram.
          if (normalizedPurpose.includes('rhythm') || normalizedPurpose.includes('arrhythm')) {
            terms.push('ECG', 'Electrocardiogram');
          }
          if (normalizedPurpose.includes('ecg')) {
            terms.push('ECG');
          }
          if (normalizedPurpose.includes('cardiac')) {
            terms.push('Cardiac');
          }
          // Fallback to token-based phrase
          const tokenPhrase = tokens.slice(0, 4).join(' ').trim();
          if (tokenPhrase) terms.push(tokenPhrase);
          // Last resort: first token
          if (tokens.length > 0) terms.push(tokens[0]);

          const uniqueTerms = Array.from(new Set(terms.map(t => t.trim()).filter(Boolean)));
          if (uniqueTerms.length === 0) {
            setError('Please add a more specific intended use / condition addressed to search EUDAMED.');
            setLoading(false);
            return;
          }

          let finalList: any[] = [];
          let finalTotal = 0;
          let used = '';

          for (const term of uniqueTerms) {
            const params: EudamedDevicesRequest = {
              page: 0,
              pageSize: 25,
              size: 25,
              iso2Code: 'en',
              sort: ['primaryDi,desc', 'versionNumber,DESC'],
              medicalPurposeProcedurePack: term,
              deviceStatusCode: 'refdata.device-model-status.on-the-market',
              languageIso2Code: 'en',
            };

            const data = await gamoraApi.getEudamedDevices(params);
            if (!isActive) return;
            const list = Array.isArray(data)
              ? data
              : (data?.content || data?.items || data?.results || data?.data || []);
            const total = typeof data?.totalElements === 'number' ? data.totalElements : (Array.isArray(list) ? list.length : 0);

            finalList = Array.isArray(list) ? list : [];
            finalTotal = total;
            used = term;

            if (total > 0 || finalList.length > 0) {
              break;
            }
          }

          setQueryUsed(used);
          setRows(finalList);
          setRecordCount(finalTotal);
        } catch (err: any) {
          if (!isActive) return;
          setError(err.response?.data?.detail || 'Failed to load EUDAMED results.');
        } finally {
          if (isActive) {
            setLoading(false);
          }
        }
      };

      load();
      return () => {
        isActive = false;
      };
    }, [workflowId]);

    const toDisplayValue = (value: any, options?: { normalizeRiskClass?: boolean }) => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        const strValue = String(value);
        if (options?.normalizeRiskClass && strValue.includes('refdata.risk-class.class-')) {
          const raw = strValue.split('refdata.risk-class.class-')[1] || '';
          const normalized = raw.replace(/-/g, '').toUpperCase();
          if (normalized === 'IIA') return 'Class IIa';
          if (normalized === 'IIB') return 'Class IIb';
          if (normalized === 'III') return 'Class III';
          if (normalized === 'I') return 'Class I';
          return `Class ${normalized}`;
        }
        return strValue;
      }
      if (typeof value === 'object') {
        if ('code' in value && typeof value.code === 'string') return value.code;
        if ('label' in value && typeof value.label === 'string') return value.label;
        return JSON.stringify(value);
      }
      return '';
    };

    const getValue = (item: any, keys: string[], options?: { normalizeRiskClass?: boolean }) => {
      for (const key of keys) {
        const value = item?.[key];
        const display = toDisplayValue(value, options);
        if (display && display.trim() !== '') {
          return display;
        }
      }
      return '';
    };

    return (
      <Box>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Loading EUDAMED results...
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
              {recordCount} records found{queryUsed ? ` (query: ${queryUsed})` : ''}.
            </Typography>
            <TableContainer component={Paper} sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>(Master) UDI-DI / EUDAMED ID</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Basic UDI-DI / EUDAMED DI</TableCell>
                    <TableCell>Trade name</TableCell>
                    <TableCell>Risk class</TableCell>
                    <TableCell>Manufacturer/Producer (and Authorised Representative) name</TableCell>
                    <TableCell>Actor ID/SRN</TableCell>
                    <TableCell align="center">View detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((item, idx) => {
                    const detailUrl =
                      item?.detailUrl ||
                      item?.url ||
                      item?.links?.detail ||
                      item?.links?.self ||
                      '';
                    return (
                      <TableRow key={item?.id || idx}>
                        <TableCell>{getValue(item, ['primaryDi', 'masterUdiDi', 'udiDi', 'eudamedId', 'id'])}</TableCell>
                        <TableCell>{getValue(item, ['versionNumber', 'version'])}</TableCell>
                        <TableCell>{getValue(item, ['basicUdiDi', 'eudamedDi', 'basicUdiDiId'])}</TableCell>
                        <TableCell>{getValue(item, ['tradeName', 'tradeNameEn', 'name'])}</TableCell>
                        <TableCell>{getValue(item, ['riskClass', 'riskClassCode', 'deviceRiskClass'], { normalizeRiskClass: true })}</TableCell>
                        <TableCell>{getValue(item, ['manufacturerName', 'manufacturerProducerName', 'producerName'])}</TableCell>
                        <TableCell>{getValue(item, ['actorIdSrn', 'actorId', 'srn'])}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (detailUrl) {
                                window.open(detailUrl, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={!detailUrl}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };

  const DocsTab: React.FC<{ workflowId?: string }> = ({ workflowId }) => {
    const [loading, setLoading] = useState(false);
    const [docs, setDocs] = useState<Array<{ stepId: string; stepTitle: string; artifactKey: string; filename: string; kind: string }>>([]);

    useEffect(() => {
      if (!workflowId) return;
      let active = true;
      (async () => {
        setLoading(true);
        try {
          const status = await workflowService.getWorkflowStatus(workflowId);
          const files: typeof docs = [];
          const seen = new Set<string>();
          (status.steps || []).forEach((s: any) => {
            const cfg = s.configuration || {};
            const artifacts = cfg.artifacts;
            if (artifacts && typeof artifacts === 'object') {
              Object.keys(artifacts).forEach((k) => {
                if (!k || !(k.endsWith('_csv') || k.endsWith('_xlsx'))) return;
                const key = `${s.id}:${k}`;
                if (seen.has(key)) return;
                seen.add(key);
                const filename = k.endsWith('_csv')
                  ? `${k.replace(/_csv$/i, '').replace(/_/g, '-')}.csv`
                  : `${k.replace(/_xlsx$/i, '').replace(/_/g, '-')}.xlsx`;
                files.push({ stepId: s.id, stepTitle: s.title || 'Step', artifactKey: k, filename, kind: k.endsWith('_xlsx') ? 'xlsx' : 'csv' });
              });
            }
          });
          if (active) setDocs(files);
        } catch {
          // ignore
        } finally {
          if (active) setLoading(false);
        }
      })();
      return () => { active = false; };
    }, [workflowId]);

    const handleDownload = async (stepId: string, artifactKey: string, filename: string) => {
      try {
        const blob = await workflowService.downloadStepArtifact(stepId, artifactKey);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    };

    if (!workflowId) {
      return (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ color: '#9ca3af', fontSize: '0.82rem' }}>
            No workflow linked — create or link a workflow to generate documents.
          </Typography>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: '#6b7280' }}>Loading documents...</Typography>
        </Box>
      );
    }

    if (docs.length === 0) {
      return (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography sx={{ color: '#9ca3af', fontSize: '0.82rem' }}>
            No documents generated yet. Complete the Evidence Mapping step to generate standards mapping and test reports.
          </Typography>
        </Box>
      );
    }

    const grouped = new Map<string, typeof docs>();
    docs.forEach((d) => {
      const arr = grouped.get(d.stepTitle) || [];
      arr.push(d);
      grouped.set(d.stepTitle, arr);
    });

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from(grouped.entries()).map(([title, items]) => (
          <Box key={title}>
            <Typography sx={{ fontWeight: 650, fontSize: '0.78rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.75 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {items.map((doc) => (
                <Box
                  key={`${doc.stepId}-${doc.artifactKey}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.25,
                    py: 0.75,
                    border: '1px solid #eceef1',
                    borderRadius: '8px',
                    bgcolor: '#fafbfc',
                    '&:hover': { bgcolor: '#f3f4f6' },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>
                      {doc.filename}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                      {doc.kind.toUpperCase()}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download sx={{ fontSize: '0.85rem !important' }} />}
                    onClick={() => handleDownload(doc.stepId, doc.artifactKey, doc.filename)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.74rem',
                      borderColor: '#e5e7eb',
                      color: '#374151',
                      borderRadius: '6px',
                      px: 1.25,
                      py: 0.35,
                      '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
                    }}
                  >
                    Download
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const addMessage = async (
    type: 'user' | 'assistant', 
    content: string, 
    regulatoryBodies?: string[], 
    sessionIdToUse?: string, 
    generationId?: string, 
    questionComplexity?: 'simple' | 'complex' | 'process',
    forms?: DetectedForm[],
    references?: Array<{ url: string; country: string }> | string[],
    citations?: MDRCitation[],
    suggestedForms?: FormSuggestion[],
    suggestedFormsJurisdiction?: string,
    workflowId?: string,
    workflowName?: string,
    generateAnswerForMessage?: boolean,
    workflowSuggestion?: WorkflowSuggestion
  ): Promise<Message> => {
    const newMessage: Message = {
      id: Date.now().toString() + Math.random().toString(36),
      type,
      content,
      timestamp: new Date(),
      regulatoryBodies,
      generationId,
      questionComplexity,
      forms,
      references,
      citations,
      suggestedForms,
      suggestedFormsJurisdiction,
      workflowId,
      workflowName,
      generateAnswer: generateAnswerForMessage,
      workflowSuggestion,
    };
    setMessages((prev) => [...prev, newMessage]);
    // Save to backend
    try {
      const sessionIdValue = sessionIdToUse || sessionId;
      if (sessionIdValue && sessionIdValue !== 'undefined' && sessionIdValue !== 'null') {
        await gamoraApi.saveChatMessage({
          session_id: sessionIdValue,
          message: content,
          sender: type,
          generation_id: generationId,  // Add generation_id for feedback
          forms: forms,  // Save forms if available
          references: references,  // Save references if available
          workflow_id: workflowId,
          workflow_name: workflowName,
          // user_id is no longer sent; backend uses Authorization token
        });
      }
    } catch (e) {
      // Optionally handle error
    }
    return newMessage;
  };

  const handleCreateWorkflowFromSuggestion = async (suggestion: WorkflowSuggestion, messageId: string) => {
    if (workflowCreateLoadingMessageId) return;
    try {
      setWorkflowCreateLoadingMessageId(messageId);
      setError(null);
      const targetMessage = messages.find((m) => m.id === messageId);

      const inferredJurisdiction =
        suggestion.inferredJurisdiction ||
        (selectedRegulation === 'all'
          ? inferJurisdictionFromText(suggestion.prompt)
          : (() => {
              const regulation = regulations.find((r: any) => r.id === selectedRegulation);
              return regulation ? regulation.jurisdiction : inferJurisdictionFromText(suggestion.prompt);
            })());

      let deviceClass = suggestion.deviceClass || (inferredJurisdiction ? inferDeviceClass(suggestion.prompt, inferredJurisdiction) : undefined);
      if (!deviceClass && inferredJurisdiction) {
        const fallbackClassByJurisdiction: Record<string, string> = {
          us_fda: 'class_ii',
          eu_mdr: 'class_iia',
        };
        deviceClass = fallbackClassByJurisdiction[inferredJurisdiction];
      }

      if (!inferredJurisdiction || !deviceClass) {
        setError('Unable to infer jurisdiction/device class. Please create a workflow manually.');
        setShowWorkflowCreator(true);
        return;
      }

      const now = new Date();
      const userChatMessage: WorkflowChatMessage = {
        id: `user-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'user',
        content: suggestion.prompt,
        timestamp: now.toISOString(),
      };
      const workflowRequest: ChatWorkflowCreateRequest = {
        name: buildWorkflowNameFromPrompt(suggestion.prompt, inferredJurisdiction),
        description: 'Chat workflow (suggested)',
        jurisdiction: inferredJurisdiction,
        device_class: deviceClass,
        device_type: '',
        device_category: '',
        chat_context: {},
        messages: [...messages, userChatMessage].map(msg => ({
          id: msg.id,
          type: msg.type,
          content: msg.content,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
        })),
        use_output_based_conversion: false,
      };

      const createdWorkflow = await workflowService.createChatWorkflow(workflowRequest);
      if (createdWorkflow?.id) {
        // Link the workflow to the *same* assistant response message that showed the suggestion.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, workflowId: createdWorkflow.id, workflowName: createdWorkflow.name }
              : m
          )
        );

        // Switch that message to the Workflow tab immediately.
        setActiveTabs((prev) => ({ ...prev, [messageId]: 0 }));

        // Persist the linkage so history reload keeps the workflow tab.
        const generationId = targetMessage?.generationId;
        if (sessionId && generationId) {
          try {
            await gamoraApi.linkWorkflowToChatMessage({
              session_id: sessionId,
              generation_id: generationId,
              workflow_id: createdWorkflow.id,
              workflow_name: createdWorkflow.name,
            });
          } catch (e) {
            // Non-fatal: UI is already linked; this only affects reload/history.
          }
        }

        onWorkflowSelect?.(createdWorkflow.id);
        setDismissedWorkflowSuggestionByMessageId((prev) => ({ ...prev, [messageId]: true }));
      }
    } catch (e) {
      console.error('Create workflow from suggestion failed:', e);
      setError('Failed to create workflow. Please try again.');
    } finally {
      setWorkflowCreateLoadingMessageId(null);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setError(null);
    sendInProgressRef.current = true;
    
    // Show user message and thinking indicator immediately
    const optimisticMsg: Message = {
      id: Date.now().toString() + Math.random().toString(36),
      type: 'user' as const,
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setLoading(true);
    
    // Analyze question complexity
    const questionComplexity = analyzeQuestionComplexity(userMessage);
    
    // Create session if it doesn't exist yet (first message)
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      try {
        const response = await gamoraApi.createNewSession();
        currentSessionId = response.session_id;
        localStorage.setItem('gamora_session_id', currentSessionId);
        historyLoadedRef.current = currentSessionId;
        setSessionId(currentSessionId);
        onSessionCreated?.(currentSessionId);
      } catch (err) {
        setError('Failed to create a new session. Please try again.');
        console.error('Session creation error:', err);
        setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
        sendInProgressRef.current = false;
        setLoading(false);
        return;
      }
    }
    
    // Save user message to backend (non-blocking for UI)
    try {
      const sessionIdValue = currentSessionId;
      if (sessionIdValue && sessionIdValue !== 'undefined' && sessionIdValue !== 'null') {
        gamoraApi.saveChatMessage({
          session_id: sessionIdValue,
          message: userMessage,
          sender: 'user',
        }).catch(() => {});
      }
    } catch (e) {
      // Silently ignore save errors — message is already shown optimistically
    }

    try {
      // Get jurisdiction for the search
      const jurisdiction = selectedRegulation === 'all' ? undefined : 
        (() => {
          const regulation = regulations.find((r: any) => r.id === selectedRegulation);
          return regulation ? regulation.jurisdiction : undefined;
        })();
      
      const response = await gamoraApi.search(userMessage, 5, currentSessionId, jurisdiction, generateAnswer);
      const regulatoryBodies = getUniqueRegulatoryBodies(response.references || []);
      
      // Fetch suggested forms for this specific question with the current jurisdiction
      let suggestedForms: FormSuggestion[] = [];
      let suggestedFormsJurisdiction: string | undefined = jurisdiction;
      try {
        const messagesForSuggestion = generateAnswer
          ? [
              { content: userMessage, type: 'user' },
              { content: response.answer, type: 'assistant' }
            ]
          : [{ content: userMessage, type: 'user' }];
        // Pass detected forms from the answer to prioritize them in suggestions
        suggestedForms = await formSuggestionService.getTopFormSuggestions(
          messagesForSuggestion,
          jurisdiction,
          3,
          response.forms // Pass detected forms from answer
        );
      } catch (err) {
        console.error('Error fetching suggested forms:', err);
        // Continue without suggested forms if there's an error
      }
      
      let workflowId: string | undefined;
      let workflowName: string | undefined;
      const jurisdictionHint = selectedRegulation === 'all'
        ? undefined
        : (() => {
            const regulation = regulations.find((r: any) => r.id === selectedRegulation);
            return regulation ? regulation.jurisdiction : undefined;
          })();
      const workflowNeed = analyzeWorkflowNeed(userMessage, jurisdictionHint);
      const shouldSuggestWorkflow = workflowNeed.score >= 3;
      const workflowSuggestion: WorkflowSuggestion | undefined =
        shouldSuggestWorkflow
          ? {
              score: workflowNeed.score,
              reasons: workflowNeed.reasons,
              prompt: userMessage,
              inferredJurisdiction: workflowNeed.inferredJurisdiction,
              deviceClass: workflowNeed.deviceClass,
            }
          : undefined;

      // Optional auto-create (admin setting). We only auto-create when explicitly enabled.
      const autoCreateEnabled = localStorage.getItem('gamora_workflow_auto_create_enabled') === 'true';
      const autoCreateThresholdRaw = localStorage.getItem('gamora_workflow_auto_create_threshold');
      const autoCreateThreshold = autoCreateThresholdRaw ? parseInt(autoCreateThresholdRaw, 10) : 5;
      const shouldAutoCreate =
        autoCreateEnabled &&
        Number.isFinite(autoCreateThreshold) &&
        workflowNeed.score >= (Number.isFinite(autoCreateThreshold) ? autoCreateThreshold : 5);

      if (shouldAutoCreate && workflowNeed.inferredJurisdiction) {
        try {
          let deviceClass = workflowNeed.deviceClass;
          if (!deviceClass) {
            const fallbackClassByJurisdiction: Record<string, string> = {
              us_fda: 'class_ii',
              eu_mdr: 'class_iia',
            };
            deviceClass = fallbackClassByJurisdiction[workflowNeed.inferredJurisdiction];
          }

          if (deviceClass) {
            const now = new Date();
            const userChatMessage: WorkflowChatMessage = {
              id: `user-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              type: 'user',
              content: userMessage,
              timestamp: now.toISOString(),
            };
            const workflowRequest: ChatWorkflowCreateRequest = {
              name: buildWorkflowNameFromPrompt(userMessage, workflowNeed.inferredJurisdiction),
              description: 'Chat workflow (auto-created)',
              jurisdiction: workflowNeed.inferredJurisdiction,
              device_class: deviceClass,
              device_type: '',
              device_category: '',
              chat_context: {},
              messages: [...messages, userChatMessage].map(msg => ({
                id: msg.id,
                type: msg.type,
                content: msg.content,
                timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
              })),
              use_output_based_conversion: false,
            };
            const createdWorkflow = await workflowService.createChatWorkflow(workflowRequest);
            if (createdWorkflow?.id) {
              workflowId = createdWorkflow.id;
              workflowName = createdWorkflow.name;
            }
          }
        } catch (e) {
          console.error('Auto-create workflow failed:', e);
        }
      }

      // Add assistant message with complexity information, forms, references, citations, suggested forms, and workflow linkage
      const assistantMessage = generateAnswer ? response.answer : '';
      await addMessage(
        'assistant',
        assistantMessage,
        regulatoryBodies,
        currentSessionId,
        response.generation_id,
        questionComplexity,
        response.forms,
        response.references,
        response.citations,  // Add MDR citations
        suggestedForms,  // Add suggested forms
        suggestedFormsJurisdiction,  // Add jurisdiction used for suggestions
        workflowId,
        workflowName,
        generateAnswer,
        workflowSuggestion
      );
      
      // Refresh token history after each exchange
      const tokens = await gamoraApi.getChatTokens(currentSessionId);
      setTokenHistory(tokens);
    } catch (err: any) {
      console.error('Search error:', err);
      
      // Check if it's a 402 Payment Required error (insufficient tokens)
      if (err.response?.status === 402) {
        const errorDetail = err.response?.data?.detail || '';
        const tokensMatch = errorDetail.match(/Available: (\d+)/);
        const requiredMatch = errorDetail.match(/Required: (\d+)/);
        
        if (tokensMatch && requiredMatch) {
          setCurrentTokens(parseInt(tokensMatch[1]));
          setRequiredTokens(parseInt(requiredMatch[1]));
          setShowTokenRecharge(true);
          setError(null); // Don't show error message, show modal instead
        } else {
          setError('Insufficient tokens. Please recharge your account.');
        }
      } else {
        setError('Failed to get response. Please try again.');
      }
    } finally {
      sendInProgressRef.current = false;
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  };
  const logoUrl = '/gamura_logos/favicon-48x48.png';// Fixed path

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      bgcolor: '#f5f6f8',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      maxHeight: '100%',
      p: { xs: 1, sm: 1.5 },
      gap: 0,
    }}>
      <Box sx={{ height: 4 }} />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Main Content - Show based on sidebar selection */}
      {!selectedWorkflowId && (
        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderRadius: '16px',
          border: '1px solid #eceef1',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          minHeight: 0,
        }}>
          {/* Scrollable Messages Area */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '0.75em', sm: '1em' },
              minHeight: 0,
              bgcolor: '#fafbfc',
            }}
          >
          {/* Loading History Indicator — only when not already in a send */}
          {loadingHistory && !loading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 10,
              gap: 1.5,
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                '& span': {
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#d1d5db',
                  display: 'inline-block',
                  animation: 'historyPulse 1.4s ease-in-out infinite',
                },
                '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                '@keyframes historyPulse': {
                  '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                  '40%': { opacity: 1, transform: 'scale(1)' },
                },
              }}>
                <span /><span /><span />
              </Box>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 500 }}>
                Loading conversation
              </Typography>
            </Box>
          )}

          {/* Welcome Header - Only show when not loading and it's a new chat */}
          {messages.length === 0 && !loading && !loadingHistory && !propSessionId && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 3, sm: 4 },
            maxWidth: 760,
            mx: 'auto',
            width: '100%',
          }}>
            <Typography sx={{
              color: '#111827',
              fontWeight: 800,
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              letterSpacing: '-0.03em',
              textAlign: 'center',
              lineHeight: 1.2,
            }}>
              What can I help you with?
            </Typography>
            <Typography sx={{
              color: '#9ca3af',
              mt: 1,
              mb: 4,
              fontSize: '0.9rem',
              textAlign: 'center',
              fontWeight: 400,
              maxWidth: 480,
            }}>
              Ask about medical device regulations, risk management, compliance documentation, and more.
            </Typography>

            <Stack spacing={1} sx={{ width: '100%', mb: 4 }}>
              {[
                "What are the requirements for manufacturing medical devices?",
                "How do I get regulatory approval for a new device?",
                "What are the quality system regulations?",
                "Tell me about medical device classification"
              ].map((text, index) => (
                <Button
                  key={index}
                  variant="text"
                  onClick={() => {
                    setInputValue(text);
                    setTimeout(() => handleSend(), 100);
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: '#374151',
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    py: 1.25,
                    px: 2,
                    fontSize: '0.875rem',
                    fontWeight: 450,
                    borderRadius: '12px',
                    lineHeight: 1.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: '#fff',
                      borderColor: '#1976d2',
                      boxShadow: '0 1px 3px rgba(25,118,210,0.12)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  {text}
                </Button>
              ))}
            </Stack>

            <Box sx={{ width: '100%' }}>
              <Typography sx={{
                color: '#9ca3af',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 1.5,
              }}>
                Popular topics
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {[
                  "Cybersecurity risk file",
                  "Risk management",
                  "Clinical evaluation",
                  "QMS / ISO 13485",
                  "Post-market surveillance",
                  "SaMD classification"
                ].map((text) => (
                  <Chip
                    key={text}
                    label={text}
                    onClick={() => {
                      setInputValue(text);
                      setTimeout(() => handleSend(), 100);
                    }}
                    size="small"
                    sx={{
                      bgcolor: '#fff',
                      border: '1px solid #e5e7eb',
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      height: 30,
                      borderRadius: '8px',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#f9fafb',
                        borderColor: '#1976d2',
                      },
                    }}
                  />
                ))}
              </Box>
              <Typography sx={{
                color: '#b0b8c4',
                display: 'block',
                mt: 2,
                fontSize: '0.75rem',
                fontWeight: 400,
                fontStyle: 'italic',
              }}>
                Tip: pick a regulatory authority in the dropdown before asking.
              </Typography>
            </Box>
          </Box>
        )}

          {/* Chat composer is rendered persistently below; do not hide it during "Thinking/Sending". */}

          {messages.map((message, idx) => {
            // Robust: match by content and sender
            const tokenInfo = tokenHistory.find(
              (t) =>
                t.message.trim() === message.content.trim() &&
                t.sender === message.type
            );
            // Intelligent content formatting based on question complexity
            let compactContent = message.content;
            if (message.type === 'assistant' && message.questionComplexity) {
              compactContent = formatContentBasedOnComplexity(message.content, message.questionComplexity);
            } else if (message.type === 'assistant') {
              // Fallback to default formatting for messages without complexity info
              compactContent = formatContentBasedOnComplexity(message.content, 'complex');
            }
            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                  mb: 0.5,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.2,
                    alignItems: 'flex-start',
                    mb: 1,
                    width: message.type === 'assistant' ? '100%' : 'auto',
                    maxWidth: message.type === 'assistant' ? '100%' : '80%',
                  }}
                >
                  <Chip
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                      color: message.type === 'user' ? '#fff' : '#495057',
                      bgcolor: message.type === 'user' 
                        ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' 
                        : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: message.type === 'user' ? 'none' : '1px solid #dee2e6',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'none',
                        boxShadow: 'none'
                      },
                      overflow: 'hidden',
                      borderRadius: '50%',
                      '& .MuiChip-avatar': {
                        margin: 0,
                        width: 32,
                        height: 32,
                      },
                      '& .MuiChip-label': {
                        padding: 0,
                      },
                    }}
                    avatar={message.type === 'user' ? 
                      <AccountCircle sx={{ fontSize: 20 }} /> : 
                      <Avatar
                        src={logoUrl}
                        alt="Gamura AI"
                        sx={{ width: 32, height: 32, bgcolor: 'transparent' }}
                        imgProps={{ style: { objectFit: 'contain' } }}
                      />
                    }
                  />
                  <Box
                    sx={{
                      px: 1,
                      py: 0.8,
                      ...(message.type === 'assistant' ? { pt: 0 } : {}),
                      bgcolor: '#fff',
                      color: '#222',
                      borderRadius: 2,
                      position: 'relative',
                      minWidth: 0,
                      wordBreak: 'break-word',
                      flex: 1,
                      boxShadow: 'none',
                      border: '1px solid #f0f0f0',
                      width: message.type === 'assistant' ? '100%' : 'auto',
                    }}
                  >
                    {message.type === 'assistant' && !message.workflowId && (() => {
                      const dismissed = dismissedWorkflowSuggestionByMessageId[message.id] === true;
                      if (dismissed) return null;
                      const prevUserMsg = idx > 0
                        ? messages.slice(0, idx).reverse().find(m => m.type === 'user')
                        : undefined;
                      if (!prevUserMsg) return null;
                      const need = _analyzeWorkflowNeed(prevUserMsg.content);
                      if (need.score < 3) return null;
                      const suggestion: WorkflowSuggestion = {
                        score: need.score,
                        reasons: need.reasons,
                        prompt: prevUserMsg.content,
                        inferredJurisdiction: need.inferredJurisdiction,
                        deviceClass: need.deviceClass,
                      };
                      return (
                        <Alert
                          severity="info"
                          sx={{ mb: 1, mt: 0.5, borderRadius: 1.5 }}
                          action={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleCreateWorkflowFromSuggestion(suggestion, message.id)}
                                disabled={workflowCreateLoadingMessageId === message.id}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                              >
                                {workflowCreateLoadingMessageId === message.id ? 'Creating…' : 'Create workflow'}
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() =>
                                  setDismissedWorkflowSuggestionByMessageId((prev) => ({ ...prev, [message.id]: true }))
                                }
                                sx={{ textTransform: 'none' }}
                              >
                                Not now
                              </Button>
                            </Box>
                          }
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                            This looks like a device/compliance project.
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                            Create a workflow to track classification → standards → testing → technical documentation.
                          </Typography>
                        </Alert>
                      );
                    })()}
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.4,
                        color: '#222',
                        fontWeight: message.type === 'user' ? 500 : 400,
                        fontSize: '0.875rem',
                        backgroundColor: 'transparent',
                        borderRadius: 0,
                        padding: 0,
                        border: 'none',
                      }}
                    >
                      {message.type === 'assistant' ? (() => {
                        // Calculate which tabs are available
                        const hasAnswer = message.generateAnswer !== false;
                        const hasReferences = message.references && Array.isArray(message.references) && message.references.length > 0;
                        const hasForms = message.forms && Array.isArray(message.forms) && message.forms.length > 0;
                        const hasCitations = message.citations && message.citations.length > 0;
                        const hasWorkflow = Boolean(message.workflowId);
                        // Always show Other Reference Forms tab for assistant messages (component will handle empty state)
                        const hasSuggested = true;
                        
                        // Logical tab indices: Workflow(0), Answer(1), References(2), Forms(3), Citations(4), Other(5), EUDAMED(6), Docs(7)
                        const visibleTabs: number[] = [];
                        if (isSuperAdminUser) {
                          if (hasWorkflow) visibleTabs.push(0);
                          if (hasAnswer) visibleTabs.push(1);
                        } else {
                          if (hasAnswer) visibleTabs.push(1);
                          if (hasWorkflow) visibleTabs.push(0);
                        }
                        if (hasReferences) visibleTabs.push(2);
                        if (hasForms) visibleTabs.push(3);
                        if (hasCitations) visibleTabs.push(4);
                        if (hasSuggested) visibleTabs.push(5);
                        visibleTabs.push(6);
                        visibleTabs.push(7);
                        
                        // Active tab uses logical keys; convert to actual index in visibleTabs
                        const defaultTab =
                          (isSuperAdminUser && hasWorkflow)
                            ? 0
                            : (hasAnswer ? 1 : (hasWorkflow ? 0 : (hasReferences ? 2 : (hasSuggested ? 5 : 0))));
                        const activeTabIndex = activeTabs[message.id] ?? defaultTab;
                        const actualTabIndex = Math.max(0, visibleTabs.indexOf(activeTabIndex));
                        
                        return (
                        <Box>
                          {/* Tabbed Interface for Assistant Messages */}
                          <Tabs
                            value={actualTabIndex}
                            onChange={(e, newValue) => {
                              const logicalIndex = visibleTabs[newValue] ?? 0;
                              setActiveTabs({ ...activeTabs, [message.id]: logicalIndex });
                            }}
                            sx={{
                              minHeight: 40,
                              mb: 1,
                              borderBottom: '1px solid #e0e0e0',
                              '& .MuiTab-root': {
                                minHeight: 40,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#6b7280',
                                px: 2,
                                mr: 1,
                                '&.Mui-selected': {
                                  color: '#111827',
                                  fontWeight: 800,
                                },
                              },
                            }}
                          >
                            {isSuperAdminUser && hasWorkflow && (
                              <Tab label="Workflow" />
                            )}
                            {hasAnswer && <Tab label={isSuperAdminUser ? "Explanation" : "Answer"} />}
                            {!isSuperAdminUser && hasWorkflow && (
                              <Tab label="Workflow" />
                            )}
                            {hasReferences && message.references && (
                              <Tab label={`References (${message.references.length})`} />
                            )}
                            {hasForms && message.forms && (
                              <Tab label={`Forms (${message.forms.length})`} />
                            )}
                            {hasCitations && message.citations && (
                              <Tab label={`Citations (${message.citations.length})`} />
                            )}
                            {hasSuggested && (
                              <Tab label="Other Reference Forms" />
                            )}
                            <Tab label="EUDAMED" />
                            <Tab label="Docs" />
                          </Tabs>

                          {/* Tab Panels */}
                          <Box
                            sx={{
                              mt: 1,
                              maxWidth: '100%',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {/* Answer Tab */}
                            {activeTabIndex === 1 && hasAnswer && (
                              <Box
                                sx={{
                                  maxWidth: '100%',
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  overflowX: 'visible',
                                }}
                              >
                                {/* Workflow suggestion banner is now rendered above the tabs */}
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={{
                                    ul: ({node, ...props}) => <ul style={{ 
                                      margin: '0.05em 0 0.05em 1.1em', 
                                      padding: 0, 
                                      listStyle: 'disc',
                                      lineHeight: 1.1,
                                      maxWidth: '100%',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }} {...props} />,
                                    ol: ({node, ...props}) => <ol style={{ 
                                      margin: '0.05em 0 0.05em 1.1em', 
                                      padding: 0, 
                                      listStyle: 'decimal',
                                      lineHeight: 1.1,
                                      maxWidth: '100%',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }} {...props} />,
                                    li: ({children, ...props}) => (
                                      <li
                                        style={{
                                          margin: '0.02em 0',
                                          padding: '0.01em 0',
                                          lineHeight: 1.1,
                                          fontSize: '0.85rem',
                                          color: '#2c2c2c',
                                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                          maxWidth: '100%',
                                          overflowWrap: 'anywhere',
                                          wordBreak: 'break-word',
                                        }}
                                        {...props}
                                      >
                                        {children}
                                      </li>
                                    ),
                                    p: ({node, children, ...props}) => {
                                      if (
                                        React.Children.toArray(children).some(
                                          child =>
                                            React.isValidElement(child) &&
                                            [
                                              'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'table'
                                            ].includes((child.type as string))
                                        )
                                      ) {
                                        return <>{children}</>;
                                      }
                                      return <span style={{ 
                                        whiteSpace: 'pre-wrap', 
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.1, 
                                        color: '#2c2c2c', 
                                        fontSize: '0.85rem',
                                        display: 'block',
                                        marginBottom: '0.05em',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                      }} {...props}>{children}</span>;
                                    },
                                    h1: ({node, children, ...props}) => <h1 style={{ 
                                      margin: '0.1em 0 0.05em 0', 
                                      fontSize: '1.0rem', 
                                      fontWeight: 700,
                                      color: '#1a1a1a',
                                      borderBottom: '1px solid #d0d0d0',
                                      paddingBottom: '0.05em',
                                      letterSpacing: '0.01em',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h1>,
                                    h2: ({node, children, ...props}) => <h2 style={{ 
                                      margin: '0.08em 0 0.05em 0', 
                                      fontSize: '0.95rem', 
                                      fontWeight: 600,
                                      color: '#1a1a1a',
                                      borderBottom: '1px solid #e8e8e8',
                                      paddingBottom: '0.03em',
                                      letterSpacing: '0.005em',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h2>,
                                    h3: ({node, children, ...props}) => <h3 style={{ 
                                      margin: '0.08em 0 0.05em 0', 
                                      fontSize: '0.9rem', 
                                      fontWeight: 600,
                                      color: '#1a1a1a',
                                      letterSpacing: '0.005em',
                                      backgroundColor: '#f8f9fa',
                                      padding: '0.1em 0.3em',
                                      borderRadius: '3px',
                                      borderLeft: '2px solid #007bff',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h3>,
                                    h4: ({node, children, ...props}) => <h4 style={{ 
                                      margin: '0.05em 0 0.03em 0', 
                                      fontSize: '0.85rem', 
                                      fontWeight: 600,
                                      color: '#2a2a2a',
                                      letterSpacing: '0.005em',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h4>,
                                    h5: ({node, children, ...props}) => <h5 style={{ 
                                      margin: '0.04em 0 0.03em 0', 
                                      fontSize: '0.8rem', 
                                      fontWeight: 600,
                                      color: '#3a3a3a',
                                      letterSpacing: '0.005em',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h5>,
                                    h6: ({node, children, ...props}) => <h6 style={{ 
                                      margin: '0.03em 0 0.02em 0', 
                                      fontSize: '0.75rem', 
                                      fontWeight: 600,
                                      color: '#3a3a3a',
                                      letterSpacing: '0.005em',
                                      lineHeight: 1.1
                                    }} {...props}>{children}</h6>,
                                    blockquote: ({node, children, ...props}) => <blockquote style={{
                                      margin: '0.15em 0',
                                      padding: '0.2em 0.5em',
                                      borderLeft: '2px solid #1976d2',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '0 2px 2px 0',
                                      fontStyle: 'italic',
                                      color: '#4a4a4a',
                                      fontSize: '0.9rem'
                                    }} {...props}>{children}</blockquote>,
                                    code: ({inline, className, children, ...props}: {inline?: boolean, className?: string, children?: React.ReactNode} & React.HTMLAttributes<HTMLElement>) => {
                                      if (inline) {
                                        return <code style={{
                                          backgroundColor: '#f0f2f5',
                                          padding: '0.1em 0.25em',
                                          borderRadius: '3px',
                                          fontSize: '0.85em',
                                          fontFamily: 'monospace',
                                          color: '#d73a49',
                                          border: '1px solid #e1e4e8'
                                        }} {...props}>{children}</code>;
                                      }
                                      return <pre style={{
                                        backgroundColor: '#f6f8fa',
                                        padding: '0.6em',
                                        borderRadius: '4px',
                                        overflow: 'auto',
                                        border: '1px solid #e1e4e8',
                                        margin: '0.3em 0',
                                        fontSize: '0.85em'
                                      }}><code style={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.9em',
                                        color: '#24292e'
                                      }} {...props}>{children}</code></pre>;
                                    },
                                    strong: ({node, children, ...props}) => <strong style={{
                                      fontWeight: 700,
                                      color: '#1a1a1a',
                                      letterSpacing: '0.01em',
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                    }} {...props}>{children}</strong>,
                                    em: ({node, children, ...props}) => <em style={{
                                      fontStyle: 'italic',
                                      color: '#2a2a2a',
                                      fontWeight: 500,
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                                    }} {...props}>{children}</em>,
                                  }}
                                >
                                  {compactContent}
                                </ReactMarkdown>
                              </Box>
                            )}

                            {/* References Tab */}
                            {activeTabIndex === 2 && hasReferences && message.references && (
                              <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                                  Source references used in generating this answer:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {message.references.map((ref, refIdx) => {
                                    const url = typeof ref === 'string' ? ref : ref.url;
                                    const country = typeof ref === 'string' ? undefined : ref.country;
                                    return (
                                      <Box 
                                        key={refIdx}
                                        sx={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: 1,
                                          p: 1.5,
                                          bgcolor: '#f8f9fa',
                                          borderRadius: 1,
                                          border: '1px solid #e0e0e0',
                                          '&:hover': { bgcolor: '#f0f0f0' },
                                        }}
                                      >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography
                                            component="a"
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{
                                              color: '#1976d2',
                                              textDecoration: 'none',
                                              fontSize: '0.875rem',
                                              '&:hover': { textDecoration: 'underline' },
                                              display: 'block',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {url}
                                          </Typography>
                                        </Box>
                                        {country && (
                                          <Chip 
                                            label={country} 
                                            size="small" 
                                            sx={{ height: 22, fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            )}

                            {/* Forms Tab */}
                            {activeTabIndex === 3 && hasForms && message.forms && (
                              <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                                  Forms detected and relevant to your query:
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  {message.forms.map((form, formIdx) => (
                                    <Box 
                                      key={form.form_id || formIdx}
                                      sx={{ 
                                        p: 2, 
                                        bgcolor: '#f8f9fa', 
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0'
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                          {form.form_code}: {form.form_name}
                                        </Typography>
                                        {form.jurisdiction && (
                                          <Chip 
                                            label={form.jurisdiction.replace('_', ' ').toUpperCase()} 
                                            size="small" 
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                      {form.description && (
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem', mb: 1.5 }}>
                                          {form.description}
                                        </Typography>
                                      )}
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {form.form_download_url && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Download sx={{ fontSize: 16 }} />}
                                            onClick={() => window.open(form.form_download_url, '_blank')}
                                            sx={{ fontSize: '0.8rem', height: 32 }}
                                          >
                                            Download Form
                                          </Button>
                                        )}
                                        {form.source_page_url && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<Launch sx={{ fontSize: 16 }} />}
                                            onClick={() => window.open(form.source_page_url, '_blank')}
                                            sx={{ fontSize: '0.8rem', height: 32 }}
                                          >
                                            View Source
                                          </Button>
                                        )}
                                        {form.checklist_download_url && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            onClick={() => window.open(form.checklist_download_url, '_blank')}
                                            sx={{ fontSize: '0.8rem', height: 32 }}
                                          >
                                            Checklist
                                          </Button>
                                        )}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Citations Tab */}
                            {activeTabIndex === 4 && hasCitations && message.citations && (
                              <Box>
                                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                                  MDR section citations referenced in this answer:
                                </Typography>
                                <MDRCitationList citations={message.citations} />
                              </Box>
                            )}

                            {/* Workflow Tab */}
                            {activeTabIndex === 0 && hasWorkflow && message.workflowId && (
                              <Box sx={{ px: 0.5 }}>
                                <WorkflowStepsPanel
                                  workflowId={message.workflowId}
                                  workflowName={message.workflowName || selectedWorkflowName || 'Workflow'}
                                  onOpenEudamedTab={() => {
                                    setActiveTabs({ ...activeTabs, [message.id]: 6 });
                                  }}
                                />
                              </Box>
                            )}

                            {/* EUDAMED Tab */}
                            {activeTabIndex === 6 && (
                              <Box>
                                <EudamedTab workflowId={message.workflowId} />
                              </Box>
                            )}

                            {/* Docs Tab */}
                            {activeTabIndex === 7 && (
                              <Box>
                                <DocsTab workflowId={message.workflowId} />
                              </Box>
                            )}

                            {/* Other Reference Forms Tab */}
                            {activeTabIndex === 5 && (
                              <Box>
                                <FormSuggestionComponent
                                  messages={messages.slice(0, idx + 1)}
                                  jurisdiction={message.suggestedFormsJurisdiction || (() => {
                                    // Priority 1: Get jurisdiction from selected regulation (most reliable)
                                    if (selectedRegulation && selectedRegulation !== 'all' && regulations.length > 0) {
                                      const regulation = regulations.find((r: any) => r.id === selectedRegulation);
                                      if (regulation && regulation.jurisdiction) {
                                        return regulation.jurisdiction;
                                      }
                                    }
                                    
                                    // Priority 2: Try to infer from ALL messages in the conversation (not just current message)
                                    const allMessagesText = messages.slice(0, idx + 1)
                                      .map(m => m.content.toLowerCase())
                                      .join(' ');
                                    
                                    if (allMessagesText.includes('eu') || allMessagesText.includes('european') || (allMessagesText.includes('mdr') && !allMessagesText.includes('imdr'))) {
                                      return 'eu_mdr';
                                    }
                                    if (allMessagesText.includes('fda') || (allMessagesText.includes('us') && allMessagesText.includes('united states'))) {
                                      return 'us_fda';
                                    }
                                    
                                    return undefined;
                                  })()}
                                  preloadedSuggestions={message.suggestedForms}
                                  preloadedJurisdiction={message.suggestedFormsJurisdiction}
                                  onFormDownload={(formId) => {
                                  }}
                                  onSuggestionsLoaded={() => {
                                    // Callback to notify when suggestions are loaded (for future use if needed)
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                        );
                      })() : (
                        message.content
                      )}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        mt: 0.4, 
                        opacity: 0.8,
                        textAlign: message.type === 'user' ? 'right' : 'left',
                        color: '#777',
                        fontSize: '0.7rem',
                        fontWeight: 500
                      }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                    {message.type === 'assistant' && (
                      <Box sx={{ mt: 0.4, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                        {message.regulatoryBodies && message.regulatoryBodies.length > 0 ? (
                          <>
                            <Typography variant="caption" sx={{ color: '#666', mr: 0.3, alignSelf: 'center', fontSize: '0.7rem', fontWeight: 500 }}>
                              Based on:
                            </Typography>
                            {message.regulatoryBodies.map((body, index) => (
                              <Chip
                                key={index}
                                label={body}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 15,
                                  color: '#555',
                                  borderColor: '#ccc',
                                  bgcolor: '#f8f9fa',
                                  fontWeight: 500,
                                  '&:hover': {
                                    bgcolor: '#f0f0f0',
                                    borderColor: '#bbb'
                                  }
                                }}
                              />
                            ))}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic', fontSize: '0.7rem' }}>
                            Regulatory body information not available
                          </Typography>
                        )}
                      </Box>
                    )}
                    {message.type === 'assistant' && tokenInfo ? (
                      <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.2, fontSize: '0.65rem' }}>
                        Tokens: {tokenInfo.tokens_used} 
                        {tokenInfo.chunking_tokens && tokenInfo.embedding_tokens && tokenInfo.retrieval_tokens && tokenInfo.generation_tokens ? (
                          <span>
                            (Chunking: {tokenInfo.chunking_tokens}, 
                            Embedding: {tokenInfo.embedding_tokens}, 
                            Retrieval: {tokenInfo.retrieval_tokens}, 
                            Generation: {tokenInfo.generation_tokens})
                          </span>
                        ) : (
                          <span>(Prompt: {tokenInfo.prompt_tokens}, Completion: {tokenInfo.completion_tokens})</span>
                        )}
                      </Typography>
                    ) : null}
                    
                    {message.type === 'assistant' && message.generationId && (
                      <FeedbackButtons 
                        generationId={message.generationId}
                        onFeedbackSubmitted={() => {}}
                        showSubmittedMessage={true}
                      />
                    )}
                  </Box>
                </Box>
                
              </Box>
            );
          })}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                <Chip
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #dee2e6',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    '& .MuiChip-avatar': { margin: 0, width: 32, height: 32 },
                    '& .MuiChip-label': { padding: 0 },
                  }}
                  avatar={
                    <Avatar
                      src={logoUrl}
                      alt="Gamura AI"
                      sx={{ width: 32, height: 32, bgcolor: 'transparent' }}
                      imgProps={{ style: { objectFit: 'contain' } }}
                    />
                  }
                />
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  bgcolor: '#fff',
                  borderRadius: '14px',
                  border: '1px solid #eceef1',
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    '& span': {
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#9ca3af',
                      display: 'inline-block',
                      animation: 'thinkingPulse 1.4s ease-in-out infinite',
                    },
                    '& span:nth-of-type(2)': { animationDelay: '0.2s' },
                    '& span:nth-of-type(3)': { animationDelay: '0.4s' },
                    '@keyframes thinkingPulse': {
                      '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                      '40%': { opacity: 1, transform: 'scale(1)' },
                    },
                  }}>
                    <span /><span /><span />
                  </Box>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 500 }}>
                    Thinking
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 2, bgcolor: '#fff', color: '#b00020', border: '1px solid #f5c6cb', fontSize: 14, fontWeight: 400 }}>
              {error}
            </Alert>
          )}

          {/* AI Literacy Notice - Show when there are AI responses and not dismissed */}
          {messages.some(m => m.type === 'assistant') && !aiNoticeDismissed && (
            <Box sx={{ mx: 2, mb: 2 }}>
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ 
                  bgcolor: '#e3f2fd', 
                  color: '#1565c0', 
                  border: '1px solid #90caf9', 
                  fontSize: 13,
                  '& .MuiAlert-icon': {
                    color: '#1976d2'
                  }
                }}
                action={
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => setShowAIDisclaimer(true)}
                      sx={{ 
                        color: '#1565c0', 
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 500,
                        minWidth: 'auto',
                        px: 1
                      }}
                    >
                      Learn More
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAiNoticeDismissed(true);
                        localStorage.setItem('gamora_ai_notice_dismissed', 'true');
                      }}
                      sx={{ color: '#1565c0', p: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  AI-Generated Content Notice
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                  Responses are generated using AI and may contain errors. Always verify critical information 
                  with official sources and consult qualified professionals for regulatory compliance matters.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Action Buttons removed */}
          </Box> {/* End of scrollable messages area */}

          {/* Persistent Chat Input */}
          {(
          <Box sx={{ 
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
            bgcolor: '#fff', 
            borderTop: '1px solid #eceef1',
            flexShrink: 0,
          }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about medical device regulations..."
              variant="outlined"
              disabled={loading || loadingHistory}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#f8f9fa',
                  height: 44,
                  fontSize: '0.875rem',
                  transition: 'all 0.15s ease',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                    transition: 'border-color 0.15s ease',
                  },
                  '&:hover': {
                    bgcolor: '#f3f4f6',
                    '& fieldset': { borderColor: '#d1d5db' },
                  },
                  '&.Mui-focused': {
                    bgcolor: '#fff',
                    '& fieldset': { borderColor: '#1976d2', borderWidth: '1.5px' },
                  },
                },
              }}
            />
            
            {/* Regulatory Authority Selection */}
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="regulation-select-label" sx={{ fontSize: '0.8rem' }}>Regulatory Authority</InputLabel>
              <Select
                labelId="regulation-select-label"
                id="regulation-select"
                value={selectedRegulation || 'all'}
                label="Regulatory Authority"
                onChange={(e) => setSelectedRegulation(e.target.value)}
                disabled={loading || loadingHistory}
                sx={{
                  bgcolor: '#f8f9fa',
                  height: 44,
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': { borderColor: '#d1d5db' },
                  '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: '1.5px' },
                }}
              >
                <MenuItem value="all">All Regulatory Authorities</MenuItem>
                {regulations.length > 0 && getRegulationOptions().map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
                {regulations.length === 0 && (
                  <MenuItem value="loading" disabled>Loading regulations...</MenuItem>
                )}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading || loadingHistory || !inputValue.trim()}
              disableElevation
              sx={{ 
                minWidth: 44,
                width: 44,
                height: 44,
                bgcolor: '#111827',
                color: '#fff',
                borderRadius: '12px',
                p: 0,
                transition: 'all 0.15s ease',
                '&:hover': { 
                  bgcolor: '#1f2937',
                },
                '&:disabled': { 
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af',
                }
              }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {loading ? <CircularProgress size={18} color="inherit" /> : <Send sx={{ fontSize: 18 }} />}
              </Box>
            </Button>
          </Box>
          </Box>
          )}
        </Box>
      )}

      {/* Workflow Steps - Show when workflow is selected from sidebar */}
      {selectedWorkflowId && (
        <Box 
          sx={{
            flex: 1,
            overflow: 'auto',
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '0.5em', sm: '0.75em' },
            minHeight: 0,
            bgcolor: '#fff',
            border: '1px solid #e6e8ec',
            borderRadius: 2,
          }}
        >
          <WorkflowStepsPanel
            workflowId={selectedWorkflowId}
            workflowName={selectedWorkflowName}
          />
        </Box>
      )}
      </Box>

      {/* Token Recharge Modal */}
      <TokenRechargeModal
        open={showTokenRecharge}
        onClose={() => setShowTokenRecharge(false)}
        currentTokens={currentTokens}
        requiredTokens={requiredTokens}
      />

      {/* Share Dialog */}
      {sessionId && (
        <ShareDialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          sessionId={sessionId}
          sessionName={messages.length > 0 ? `Chat with ${messages.length} messages` : undefined}
        />
      )}

      {/* Workflow Creator Modal */}
      <WorkflowCreator
        open={showWorkflowCreator}
        onClose={() => setShowWorkflowCreator(false)}
        onWorkflowCreated={handleWorkflowCreated}
      />

      {/* Convert to Workflow Dialog */}
      <ConvertToWorkflowDialog
        open={showConvertToWorkflow}
        onClose={() => setShowConvertToWorkflow(false)}
        messages={messages}
        onWorkflowCreated={handleWorkflowCreated}
      />

      {/* AI Literacy Disclaimer Modal */}
      <AILiteracyDisclaimer
        open={showAIDisclaimer}
        onClose={() => setShowAIDisclaimer(false)}
      />
    </Box>
  );
};

export default SearchInterface; 